<script lang="ts">
  import { _ } from 'svelte-i18n';
  import type { ContentItem, ContentType, Locale } from '../../content/loader';
  import ContentCard from './ContentCard.svelte';
  
  interface Props {
    title: string;
    items: ContentItem[];
    type: ContentType;
    locale: Locale;
    showViewAll?: boolean;
    viewAllUrl?: string;
  }
  
  let { 
    title, 
    items, 
    type, 
    locale, 
    showViewAll = false, 
    viewAllUrl = '' 
  }: Props = $props();
  
  function getTypeIcon(type: ContentType): string {
    switch (type) {
      case 'blog': return '📝';
      case 'changelog': return '🚀';
      case 'press': return '📰';
      case 'stories': return '💼';
      default: return '📄';
    }
  }
</script>

<div class="related-content">
  <div class="related-content__header">
    <h2 class="related-content__title">
      <span class="related-content__icon">{getTypeIcon(type)}</span>
      {title}
    </h2>
    
    {#if showViewAll && viewAllUrl && items.length > 0}
      <a href={viewAllUrl} class="related-content__view-all">
        {$_('common.viewAll')} →
      </a>
    {/if}
  </div>
  
  {#if items.length > 0}
    <div class="related-content__grid">
      {#each items as item}
        <ContentCard {item} {locale} compact={false} />
      {/each}
    </div>
  {:else}
    <div class="related-content__empty">
      <div class="empty__icon">🔍</div>
      <h3 class="empty__title">
        {$_('content.related.empty.title')}
      </h3>
      <p class="empty__description">
        {$_('content.related.empty.description')}
      </p>
    </div>
  {/if}
</div>

<style>
  .related-content {
    width: 100%;
  }
  
  .related-content__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-6);
    gap: var(--space-4);
  }
  
  .related-content__title {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin: 0;
  }
  
  .related-content__icon {
    font-size: var(--font-size-lg);
  }
  
  .related-content__view-all {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--brand-primary);
    text-decoration: none;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    transition: var(--transition-colors);
    white-space: nowrap;
  }
  
  .related-content__view-all:hover {
    background: var(--brand-primary-light);
    color: var(--brand-primary-dark);
  }
  
  .related-content__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
  }
  
  .related-content__empty {
    text-align: center;
    padding: var(--space-12) var(--space-4);
    background: var(--bg-secondary);
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-light);
  }
  
  .empty__icon {
    font-size: 3rem;
    margin-bottom: var(--space-4);
    opacity: 0.6;
  }
  
  .empty__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }
  
  .empty__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.5;
  }
  
  @media (max-width: 768px) {
    .related-content__header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .related-content__title {
      font-size: var(--font-size-lg);
    }
    
    .related-content__grid {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
    
    .related-content__empty {
      padding: var(--space-8) var(--space-3);
    }
  }
</style>
